import type { InvitationPublicOut } from "@sagot/api-client";
import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon, initialsOf } from "@sagot/ui";
import { useState } from "react";

/** `module.action` keys → [module, actions[]] in first-seen order. */
export function groupPermissions(keys: readonly string[]): [string, string[]][] {
  const groups = new Map<string, string[]>();
  for (const key of keys) {
    const [module, action] = key.split(".");
    if (!module || !action) continue;
    groups.set(module, [...(groups.get(module) ?? []), action]);
  }
  return [...groups.entries()];
}

const RESTRICTED_MODULES = ["billing", "team", "api_keys"] as const;

function formatExpiry(expiresAt: string, locale: string) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  const days = Math.max(0, Math.ceil(ms / 86_400_000));
  const date = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: "numeric" }).format(new Date(expiresAt));
  return { days, date };
}

function PermissionList({ permissions }: { permissions: readonly string[] }) {
  const { t } = useTranslation();
  const groups = groupPermissions(permissions);
  const missing = RESTRICTED_MODULES.filter((module) => !groups.some(([m]) => m === module));
  return (
    <ul className="mt-2.5 space-y-1.5 text-xs text-fg-muted">
      {groups.map(([module, actions]) => (
        <li key={module} className="flex gap-2">
          <Icon name="check" className="mt-0.5 size-3.5 text-success" />
          <span>
            <span className="font-semibold text-fg">{t(`roles.modules.${module}`, { defaultValue: module })}</span>: {actions.map((a) => t(`roles.actions.${a}`, { defaultValue: a })).join(", ")}
          </span>
        </li>
      ))}
      {missing.length > 0 ? (
        <li className="flex gap-2">
          <Icon name="x" className="mt-0.5 size-3.5 text-danger" />
          <span>{t("auth.invite.cannot", { modules: missing.map((m) => t(`roles.modules.${m}`).toLowerCase()).join(", ") })}</span>
        </li>
      ) : null}
    </ul>
  );
}

/** Invite card (accept-invite.html): who, where, role, expiry, and what the role can do. */
export function InviteSummary({ invitation }: { invitation: InvitationPublicOut }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const role = invitation.role_names.join(", ");
  const article = /^[aeiou]/i.test(role) ? t("auth.invite.articleAn") : t("auth.invite.articleA");
  const expiry = formatExpiry(invitation.expires_at, i18n.language === "fil" ? "fil-PH" : "en-PH");
  const lead = invitation.inviter_name
    ? t("auth.invite.invitedYou", { inviter: invitation.inviter_name, workspace: invitation.workspace_name, product: PRODUCT_NAME })
    : t("auth.invite.invitedYouNoInviter", { workspace: invitation.workspace_name, product: PRODUCT_NAME });
  return (
    <section className="card overflow-hidden" aria-label={invitation.workspace_name}>
      <div className="card-body">
        <div className="flex items-start gap-3.5">
          <span aria-hidden="true" className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-signal-500 text-sm font-extrabold text-ink-950">
            {initialsOf(invitation.workspace_name)}
          </span>
          <div className="min-w-0">
            <p className="text-[15px] leading-snug text-fg">
              {lead} <span className="badge badge-brand align-middle">{role}</span>
            </p>
            {invitation.message ? <p className="mt-1.5 text-xs text-fg-subtle">“{invitation.message}”</p> : null}
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-px border-t border-line bg-line text-xs">
        <div className="bg-surface-2 px-5 py-3">
          <dt className="font-semibold text-fg-subtle">{t("auth.invite.workspace")}</dt>
          <dd className="mt-0.5 truncate font-bold text-fg">{invitation.workspace_name}</dd>
        </div>
        <div className="bg-surface-2 px-5 py-3">
          <dt className="font-semibold text-fg-subtle">{t("auth.invite.role")}</dt>
          <dd className="mt-0.5 font-bold text-fg">{role}</dd>
        </div>
        <div className="bg-surface-2 px-5 py-3">
          <dt className="font-semibold text-fg-subtle">{t("auth.invite.invitedBy")}</dt>
          <dd className="mt-0.5 font-bold text-fg">{invitation.inviter_name ?? "—"}</dd>
          {invitation.inviter_title ? <dd className="text-fg-subtle">{invitation.inviter_title}</dd> : null}
        </div>
        <div className="bg-surface-2 px-5 py-3">
          <dt className="font-semibold text-fg-subtle">{t("auth.invite.expires")}</dt>
          <dd className="mt-0.5 font-bold text-warning">
            {expiry.days === 0 ? t("auth.invite.expiresToday", { date: expiry.date }) : t("auth.invite.expiresIn", { count: expiry.days, date: expiry.date })}
          </dd>
        </div>
      </dl>
      <div className="border-t border-line px-5 py-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls="invite-permissions"
          className="flex w-full items-center justify-between text-xs font-semibold text-fg-muted hover:text-fg"
          onClick={() => { setOpen((v) => !v); }}
        >
          {t("auth.invite.whatCan", { article, role })}
          <Icon name={open ? "chevron-up" : "chevron-down"} className="size-4" />
        </button>
        <div id="invite-permissions" hidden={!open}>
          <PermissionList permissions={invitation.permissions} />
        </div>
      </div>
    </section>
  );
}
